/**
 * The automatic sync: a timer that lives in the SHELL, and the one line each
 * screen shows about it.
 *
 * ── WHY THE SHELL AND NOT THE SCREENS ────────────────────────────────────
 * A timer inside the TikTok screen only ticks while TikTok is open, and the
 * request was for all three to stay current WITHOUT visiting any of them. So it
 * is mounted once, above the routes, and runs whichever screen is showing.
 *
 * It runs for whoever is signed in. That is deliberate and no wider than what
 * the buttons already allow — any signed-in user can press Sync now on these
 * screens — and it is safe to run from several browsers at once: a sync writes
 * only the orders the store does not already agree with (`changedOrders`), the
 * ticks are jittered apart, and a last-run stamp in localStorage makes the tabs
 * of one browser share a single timer. Each screen has a Pause for the person
 * who does not want their browser doing it.
 */
import { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { T, SCALE } from "../../theme/paperSkin";
import {
  describeResult, isDue, nextDelay,
} from "./autoSyncPlan";
import { runAutoSync } from "./autoSyncRunner";
import {
  getAutoSyncState, readLastRun, setAutoSyncEnabled, useAutoSyncState, writeLastRun,
  type AutoChannel,
} from "./autoSyncStore";

/** Mount once, inside the signed-in shell. Renders nothing. */
export function AutoSyncRunner() {
  const { accounts } = useMsal();
  const me = accounts[0]?.username || accounts[0]?.name || "";
  const { enabled } = useAutoSyncState();

  useEffect(() => {
    if (!me || !enabled) return;
    let cancelled = false;
    let timer: number | undefined;

    const schedule = (): void => {
      timer = window.setTimeout(() => { void tick(); }, nextDelay(Date.now(), readLastRun(), Math.random()));
    };

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      // Re-checked at the moment of running, not when the timer was set: a
      // sibling tab, or a run that started while this one waited, has already
      // done the work.
      if (isDue(Date.now(), readLastRun()) && navigator.onLine && !getAutoSyncState().running) {
        // Stamped BEFORE the run, so a second tab that wakes during it stands
        // down — and so a failing run is retried in 15 minutes, not in a loop.
        writeLastRun(Date.now());
        try { await runAutoSync(me); } catch { /* every channel reports its own failure */ }
      }
      if (!cancelled) schedule();
    };

    schedule();
    return () => { cancelled = true; if (timer !== undefined) window.clearTimeout(timer); };
  }, [me, enabled]);

  return null;
}

/**
 * The line on a channel's screen: when it last ran, what it did, and — never
 * hidden — why it did not. A scheduler nobody can see is one nobody trusts, and
 * one that fails without saying so is worse than no scheduler.
 */
export function AutoSyncStatus({ channel }: { channel: AutoChannel }) {
  const { enabled, running, results } = useAutoSyncState();
  const r = results[channel];
  const bad = enabled && !running && r && r.status !== "ok";
  return (
    <div
      role="status"
      style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        fontSize: SCALE.status, color: bad ? T.text : T.textDim, padding: "2px 2px 6px",
      }}
    >
      <span style={bad ? { fontWeight: 700 } : undefined}>{describeResult(r, enabled, running)}</span>
      <button
        type="button"
        onClick={() => setAutoSyncEnabled(!enabled)}
        title={enabled
          ? "Stop this browser syncing on its own. Sync now still works."
          : "Let this browser sync the three stores every 15 minutes while i-Synergy is open."}
        style={{
          background: "transparent", border: `1px solid ${T.border}`, color: T.text,
          borderRadius: 6, padding: "1px 8px", fontSize: SCALE.status, fontWeight: 700,
          fontFamily: "inherit", cursor: "pointer",
        }}
      >{enabled ? "Pause" : "Resume"}</button>
    </div>
  );
}
